/**
 * @file components/billing/drawerStyles.js
 * @description One type scale, spacing scale and shared style helpers for the whole
 *   Billing page and its "Manage subscription" drawer. Every section reads from here
 *   so sizes, weights, badges, cards and buttons stay consistent. Inline styles only
 *   (project rule); colors come from the theme tokens `T`, sizes from the app scale
 *   `SIZE` in theme.js (owner call 2026-10-18: no oversized controls).
 *
 *   Type scale (one family, Inter via T.fontBody, numbers use tabular figures and
 *   never a second font):
 *     label   11  eyebrows, badges, tiny captions         (SIZE.label)
 *     caption 12  helper text, secondary lines, meta      (SIZE.textSm)
 *     body    13  default text, buttons, list items       (SIZE.text)
 *     title   15  card and section titles, drawer title   (SIZE.title)
 *     page    21  the page title
 *     price   28  the one big number on a plan card
 *   Weights: regular 400, medium 500, semibold 600, bold 700.
 *   Spacing (px): 4, 8, 12, 16, 20.
 *   Controls: 32 tall on desktop, 40 on phones; the primary confirm button is 40 / 44.
 *   Corners: SIZE.radius (8) on controls, 12 on cards.
 */
import { T, SIZE } from "../../theme";

export const FS = { label: SIZE.label, caption: SIZE.textSm, body: SIZE.text, title: SIZE.title, page: 21, price: 28 };
export const FW = { regular: 400, medium: 500, semibold: 600, bold: 700 };
export const SP = { xs: 4, sm: 8, md: 12, lg: 16, xl: 20 };

/** Height of a regular control (button, stepper, input) on desktop / phone. */
export const controlH = (isMobile) => (isMobile ? SIZE.controlTouch : SIZE.control);
/** Height of the primary confirm button (Subscribe, Switch cycle) on desktop / phone. */
export const primaryH = (isMobile) => (isMobile ? 44 : 40);

/** Danger palette shared by cancel and payment-failed states (kept in one place). */
export const DANGER = { text: "#9A1D1D", border: "#FCA5A5", soft: "#FECACA", bg: "#FEF2F2" };
export const WARN = { text: "#92400E", border: "#F59E0B", bg: "#FFFBEB" };
// Plan identity colors on Billing: the plan name, the Pro tiles and notes on the openings slider.
export const PLAN_TONE = { starter: T.green, pro: "#6D28D9" };

const base = { fontFamily: T.fontBody, margin: 0 };

export const text = {
  caption: { ...base, fontSize: FS.caption, fontWeight: FW.regular, lineHeight: 1.5, color: T.textSubtle },
  body: { ...base, fontSize: FS.body, fontWeight: FW.regular, lineHeight: 1.5, color: T.textMid },
  strong: { ...base, fontSize: FS.body, fontWeight: FW.semibold, lineHeight: 1.4, color: T.text },
  title: { ...base, fontSize: FS.title, fontWeight: FW.bold, lineHeight: 1.3, color: T.text, letterSpacing: "-0.01em" },
  page: { ...base, fontSize: FS.page, fontWeight: FW.bold, lineHeight: 1.25, color: T.text, letterSpacing: "-0.02em" },
  eyebrow: {
    ...base, fontSize: FS.label, fontWeight: FW.bold, lineHeight: 1.3, color: T.textSubtle,
    textTransform: "uppercase", letterSpacing: "0.06em",
  },
  num: { fontVariantNumeric: "tabular-nums" },
};

/** Card surface used by every Billing section. */
export const card = (isMobile, extra = {}) => ({
  background: "#fff",
  borderRadius: T.radiusMd,
  border: `1px solid ${T.border}`,
  padding: isMobile ? SP.lg : SP.xl,
  boxSizing: "border-box",
  ...extra,
});

/** Pill badge: CURRENT PLAN, BEST VALUE, SAVE x%. One style, color varies. */
export const pill = (color, bg, extra = {}) => ({
  display: "inline-flex", alignItems: "center", whiteSpace: "nowrap",
  fontFamily: T.fontBody, fontSize: FS.label, fontWeight: FW.bold,
  letterSpacing: "0.04em", textTransform: "uppercase", lineHeight: 1,
  color, background: bg, padding: "4px 8px", borderRadius: T.radiusFull,
  ...extra,
});

/**
 * Button style. kind: primary | secondary | quiet | danger | dangerSolid | warn | pause.
 * Options: isMobile (32 -> 40 tall), large (the primary confirm button: 40 -> 44),
 * disabled, fullWidth.
 */
export const button = (kind = "secondary", { isMobile = false, large = false, disabled = false, fullWidth = false } = {}) => {
  const kinds = {
    primary: { background: T.blue, color: "#fff", border: "1px solid transparent" },
    secondary: { background: "#fff", color: T.text, border: `1px solid ${T.borderStrong}` },
    quiet: { background: "#fff", color: T.textMid, border: `1px solid ${T.border}` },
    danger: { background: "#fff", color: DANGER.text, border: `1px solid ${DANGER.border}` },
    dangerSolid: { background: DANGER.text, color: "#fff", border: "1px solid transparent" },
    warn: { background: WARN.bg, color: WARN.text, border: `1px solid ${WARN.border}` },
    pause: { background: T.statusReview, color: "#fff", border: "1px solid transparent" },
  };
  return {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: SP.sm,
    minHeight: large ? primaryH(isMobile) : controlH(isMobile), padding: `0 ${SP.lg}px`, borderRadius: SIZE.radius,
    fontFamily: T.fontBody, fontSize: FS.body, fontWeight: FW.semibold, lineHeight: 1.2,
    cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1,
    whiteSpace: "nowrap", boxSizing: "border-box",
    ...(fullWidth ? { width: "100%" } : {}),
    ...(kinds[kind] || kinds.secondary),
  };
};

/** Status strip (plan active, payment failed, notices). tone: success | info | danger | warn */
export const notice = (tone = "success", isMobile = false) => {
  const tones = {
    success: { background: T.greenLight, color: T.green, border: `1px solid ${T.green}33` },
    info: { background: T.blueLight, color: T.blue, border: `1px solid ${T.blue}22` },
    danger: { background: DANGER.bg, color: DANGER.text, border: `1px solid ${DANGER.border}` },
    warn: { background: T.yellowLight, color: T.yellow, border: `1px solid ${T.yellow}44` },
  };
  return {
    display: "flex", alignItems: "center", justifyContent: "center", gap: SP.sm,
    minHeight: controlH(isMobile), padding: `${SP.sm}px ${SP.md}px`, borderRadius: SIZE.radius,
    fontFamily: T.fontBody, fontSize: FS.body, fontWeight: FW.semibold, lineHeight: 1.4,
    textAlign: "center", boxSizing: "border-box",
    ...(tones[tone] || tones.success),
  };
};
